// @ts-strict-ignore
import * as React from 'react';
import Alert from 'antd/lib/alert';
import TypographyLink from 'antd/lib/typography/Link';
import { useDispatch } from 'react-redux';
import { tryUpdateRelease, UpdateSelectedBuildRequest } from '../../actions/release';
import { ReleaseUpdatable } from '../../actions/updatable';
import { AppPlatform, AppStoreAvailableBuild, AppStoreType, RunwayResponseType } from '../../types';
import {
    displayBuildTypeForPlatformAndAppStoreType,
    displayStoreNameForAppStoreType,
} from '../../helpers/displayHelpers';
import { Nullable, ReleaseComponentProps, StepRunComponentProps } from '../../typescriptHelpers';
import { globalMessage } from '../../utils/globalMessage';
import ChangeSelectedBuildModalBase, {
    BuildSelectionField,
} from '@/components/selectedAppStoreBuild/ChangeSelectedBuildsModalBase';
import { useCallUpdatable, useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { StepRunUpdatable, UpdateSelectedBuildsInAppStoreRequest } from '@/actions/stepRun';
import { getIsGooglePlayStore } from '@/helpers';
import { isEqual, sortBy } from 'lodash';
import { useHistory } from 'react-router-dom';
import { addShowStepConfigSettingsParam } from '@/helpers/routeHelpers';

const SELECTED_BUILDS_FIELD = 'selectedAppStoreBuilds';
const RETAINED_BUILDS_FIELD = 'retainedAppStoreBuilds';

interface ChangeSelectedBuildModalProps extends ReleaseComponentProps {
    platform: AppPlatform;
    appStoreType: AppStoreType;
    includeAssociatedCIBuilds: boolean;
    open: boolean;
    currentlySelectedAppStoreBuilds?: AppStoreAvailableBuild[];
    currentlyRetainedAppStoreBuilds?: AppStoreAvailableBuild[];
    retainedBuildsManuallySet?: boolean;
    availableBuilds: AppStoreAvailableBuild[];
    onClose: () => void;
    onSuccess: () => void;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
}

const ChangeSelectedBuildModal: React.FC<ChangeSelectedBuildModalProps> = (props: ChangeSelectedBuildModalProps) => {
    const dispatch = useDispatch();
    const history = useHistory();

    const openStepSettings = () => {
        props.onClose();
        history.push({ search: addShowStepConfigSettingsParam(history.location.search) });
    };

    const isGooglePlay = getIsGooglePlayStore(props.appStoreType);
    // A Play release can carry several bundles / APKs at once
    const selectsMultipleBuilds = isGooglePlay;
    // Retained builds are a Play Console concept: older builds kept in the submission to support older Android versions
    const supportsRetainedBuilds = isGooglePlay;

    const [
        updateSelectedBuildStepRun,
        updateSelectedBuildIsLoadingStepRun,
        updateSelectedBuildResponseTypeStepRun,
        updateSelectedBuildErrorStepRun,
    ] = useCallUpdatable(StepRunUpdatable.UpdateSelectedBuildsInAppStore, props.stepRunComponentProps);

    const updateSelectedBuildSuccessReleaseStep = useUpdated(ReleaseUpdatable.UpdateSelectedBuild, props.releaseId);
    const updateSelectedBuildIsLoadingReleaseStep = useIsLoading(ReleaseUpdatable.UpdateSelectedBuild, props.releaseId);
    const updateSelectedBuildErrorReleaseStep = useError(ReleaseUpdatable.UpdateSelectedBuild, props.releaseId);

    const updateSelectedBuildSuccess =
        props.stepRunComponentProps ?
            updateSelectedBuildResponseTypeStepRun === RunwayResponseType.Success
        :   updateSelectedBuildSuccessReleaseStep;
    const updateSelectedBuildIsLoading =
        props.stepRunComponentProps ? updateSelectedBuildIsLoadingStepRun : updateSelectedBuildIsLoadingReleaseStep;
    const updateSelectedBuildError =
        props.stepRunComponentProps ? updateSelectedBuildErrorStepRun : updateSelectedBuildErrorReleaseStep;

    const onSaveChanges = (buildIdsByFieldName: Record<string, string[]>) => {
        const selectedBuildIds = buildIdsByFieldName[SELECTED_BUILDS_FIELD] ?? [];
        const retainedBuildIds = buildIdsByFieldName[RETAINED_BUILDS_FIELD] ?? [];

        const selectedBuildsChanged = !haveSameBuildIds(selectedBuildIds, props.currentlySelectedAppStoreBuilds);
        const retainedBuildsChanged =
            supportsRetainedBuilds && !haveSameBuildIds(retainedBuildIds, props.currentlyRetainedAppStoreBuilds);

        if (!selectedBuildsChanged && !retainedBuildsChanged) {
            props.onClose();

            return;
        }

        const request: UpdateSelectedBuildRequest = {
            orgId: props.orgId,
            appId: props.appId,
            releaseId: props.releaseId,
            buildIds: selectedBuildIds,
            // Left out unless it changed, so the backend knows not to touch the retained builds
            retainedBuildIds: retainedBuildsChanged ? retainedBuildIds : undefined,
        };

        if (props.stepRunComponentProps) {
            updateSelectedBuildStepRun(request as UpdateSelectedBuildsInAppStoreRequest);
        } else {
            dispatch(tryUpdateRelease(ReleaseUpdatable.UpdateSelectedBuild, request, props.releaseId));
        }
    };

    React.useEffect(() => {
        if (updateSelectedBuildSuccess) {
            globalMessage.success(successMessageForAppStoreType(props.appStoreType));
            props.onSuccess();
        }
    }, [updateSelectedBuildSuccess]);

    React.useEffect(() => {
        if (updateSelectedBuildError) {
            globalMessage.error(updateSelectedBuildError);
        }
    }, [updateSelectedBuildError]);

    const title = `Change selected ${displayBuildTypeForPlatformAndAppStoreType(
        props.platform,
        props.appStoreType,
    )} in ${displayStoreNameForAppStoreType(props.appStoreType)}`;

    const fields: BuildSelectionField[] = [
        {
            name: SELECTED_BUILDS_FIELD,
            label: selectBuildLabelForAppStoreType(props.appStoreType),
            required: true,
            multiple: selectsMultipleBuilds,
            showRecentBuilds: true,
            showPastBuildsEmptyState: false,
            currentSelection: props.currentlySelectedAppStoreBuilds,
        },
    ];

    if (supportsRetainedBuilds) {
        fields.push({
            name: RETAINED_BUILDS_FIELD,
            label: 'Retain build(s) from a previous release',
            required: false,
            multiple: true,
            showRecentBuilds: false,
            showPastBuildsEmptyState: true,
            showsClearButton: true,
            currentSelection: props.currentlyRetainedAppStoreBuilds,
            excludeBuildsSelectedInField: SELECTED_BUILDS_FIELD,
            excludeReason: 'This build is already selected for this release, so it does not need to be retained.',
            labelTooltip:
                'Retained builds will be automatically carried over to future releases. If you wish to stop retaining these builds, clear them from this field and save.',
        });
    }

    return (
        <ChangeSelectedBuildModalBase
            platform={props.platform}
            appStoreType={props.appStoreType}
            title={title}
            isLoading={updateSelectedBuildIsLoading}
            includeAssociatedCIBuilds={props.includeAssociatedCIBuilds}
            open={props.open}
            fields={fields}
            availableBuilds={props.availableBuilds}
            onSaveChanges={onSaveChanges}
            renderRetainedBuildsBanner={
                supportsRetainedBuilds ?
                    (buildIdsByFieldName) =>
                        buildRetainedBuildsBanner(
                            buildIdsByFieldName[RETAINED_BUILDS_FIELD] ?? [],
                            props.retainedBuildsManuallySet,
                            props.currentlyRetainedAppStoreBuilds,
                            props.availableBuilds,
                            props.stepRunComponentProps ? openStepSettings : undefined,
                        )
                :   undefined
            }
            onClose={props.onClose}
            onSuccess={props.onSuccess}
            releaseId={props.releaseId}
            appId={props.appId}
            orgId={props.orgId}
        />
    );
};

export default ChangeSelectedBuildModal;

const haveSameBuildIds = (buildIds: string[], builds: Nullable<AppStoreAvailableBuild[]>) => {
    return isEqual(sortBy(buildIds), sortBy((builds ?? []).map((build) => build.appStoreBuild.id)));
};

const selectBuildLabelForAppStoreType = (appStoreType: AppStoreType) => {
    switch (appStoreType) {
        case AppStoreType.Apple:
            return 'Select a build';
        case AppStoreType.Google:
            return 'Select App Bundle / APK';
        case AppStoreType.Amazon:
            return 'Select APK';
        // intentionally left blank for Huawei as we can't select builds
        default:
            return 'Select ';
    }
};

const successMessageForAppStoreType = (appStoreType: AppStoreType) => {
    switch (appStoreType) {
        case AppStoreType.Apple:
            return 'Build updated in App Store Connect!';
        case AppStoreType.Google:
            return 'App Bundle / APK updated in the Play Console!';
        case AppStoreType.Amazon:
            return 'APK updated in the Amazon Appstore!';
        // intentionally left blank for Huawei as we can't select builds
        default:
            return null;
    }
};

const buildRetainedBuildsBanner = (
    selectedBuildIds: string[],
    isSavedAsManuallySet: boolean | undefined,
    savedRetainedBuilds: Nullable<AppStoreAvailableBuild[]>,
    availableBuilds: AppStoreAvailableBuild[],
    onOpenStepSettings: (() => void) | undefined,
): React.ReactNode => {
    // An unsaved edit already overrides the settings, so the banner switches before anything is saved
    const isManuallySet = isSavedAsManuallySet || !haveSameBuildIds(selectedBuildIds, savedRetainedBuilds);
    const retainedBuilds = availableBuilds.filter((build) => selectedBuildIds.includes(build.appStoreBuild.id));
    const buildNumbers = retainedBuilds.map((build) => `#${build.appStoreBuild.buildNumber}`).join(', ');
    if (!isManuallySet && !buildNumbers) {
        return null;
    }

    const settingsLink =
        onOpenStepSettings ?
            <TypographyLink onClick={onOpenStepSettings}>your step settings</TypographyLink>
        :   'your step settings';

    const isSingular = retainedBuilds.length === 1;

    if (isManuallySet) {
        return (
            <Alert
                type="warning"
                showIcon
                style={{ marginTop: -12 }}
                message={
                    buildNumbers ?
                        <>
                            {isSingular ? 'Build' : 'Builds'} {buildNumbers} {isSingular ? 'is' : 'are'} retained for
                            this release only, overriding {settingsLink}.
                        </>
                    :   <>Nothing is retained for this release, overriding {settingsLink}.</>
                }
            />
        );
    }

    return (
        <Alert
            type="info"
            showIcon
            style={{ marginTop: -12 }}
            message={
                <>
                    {isSingular ? 'Build' : 'Builds'} {buildNumbers} {isSingular ? 'was' : 'were'} automatically
                    selected to be retained. Changing this selection overrides {settingsLink} for this release only.
                </>
            }
        />
    );
};
